<script setup lang="ts">
import ConnectionCredentialsDialog from '@/views/external-launch/ConnectionCredentialsDialog.vue'
import ConnectionDialog from '@/views/external-launch/ConnectionDialog.vue'
import type { ConnectionData, ConnectionStatus } from '@/views/external-launch/types'
import { formatDateToMonthShort } from '@core/utils/formatters'
import { getDateLocale } from '@core/utils/locale'
import { userIsAdmin, userIsScopedAdmin } from '@/utils/helpers'

// Секрет системы обучения даёт доступ к данным учеников клиента, поэтому подключения
// настраивают только администраторы платформы: `manage scenarios` есть у `admin` (через
// `manage all`) и у `scoped_admin`, но не у менеджера организации.
definePage({
  meta: {
    action: 'manage',
    subject: 'scenarios',
  },
})

const { t } = useI18n()
const { locale } = useI18n({ useScope: 'global' })
const router = useRouter()
const dateLocale = getDateLocale(locale.value)
const userData = useCookie<any>('userData')

const userTimeZone = computed(
  () => userData.value?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC',
)

// Тот же снапшот признака, что гейтит пункт меню. Это не защита — бэкенд на выключенном
// признаке и так отвечает 403, — а чтобы прямой заход по адресу не показывал пустой раздел.
const orgSettings = useCookie<any>('organizationSettings')
const userRoles: string[] = userData.value?.roles ?? []

if (!(orgSettings.value?.enabledFeatures ?? []).includes('external-launch'))
  await router.replace('/games/list')

// Роль дублирует гейт Casl из `definePage`: снапшот `enabledFeatures` менеджеру тоже
// выдаётся (публикации и журнал ему нужны), и без этой проверки менеджер увидел бы страницу
// подключений на долю секунды до отказа бэкенда.
if (!userIsAdmin(userRoles) && !userIsScopedAdmin(userRoles))
  await router.replace('/external-launch/publications')

const connections = ref<ConnectionData[]>([])
const isLoading = ref(true)
const loadFailed = ref(false)

const isSnackbarVisible = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('success')

const isConnectionDialogVisible = ref(false)
const isCredentialsDialogVisible = ref(false)
const activeConnection = ref<ConnectionData | null>(null)

const headers = [
  { title: t('externalLaunch.manage.connections.headers.account'), key: 'accountKey' },
  { title: t('externalLaunch.manage.connections.headers.base_url'), key: 'baseUrl' },
  { title: t('externalLaunch.manage.connections.headers.client_id'), key: 'clientId' },
  { title: t('externalLaunch.manage.connections.headers.secret'), key: 'secretLast4', sortable: false },
  { title: t('externalLaunch.manage.connections.headers.status'), key: 'status' },
  { title: t('externalLaunch.manage.connections.headers.expires_at'), key: 'expiresAt' },
  { title: t('externalLaunch.manage.connections.headers.actions'), key: 'actions', sortable: false },
]

const notify = (text: string, color: string = 'success') => {
  snackbarText.value = text
  snackbarColor.value = color
  isSnackbarVisible.value = true
}

const fetchConnections = async () => {
  isLoading.value = true
  loadFailed.value = false

  try {
    connections.value = (await $api<ConnectionData[]>('/external-launch/connections')) ?? []
  }
  catch {
    connections.value = []
    loadFailed.value = true
  }
  finally {
    isLoading.value = false
  }
}

await fetchConnections()

const statusColor = (status: ConnectionStatus): string => {
  if (status === 'active')
    return 'success'
  if (status === 'expired')
    return 'warning'
  if (status === 'disabled')
    return 'secondary'

  return 'info'
}

const statusLabel = (status: ConnectionStatus): string =>
  t(`externalLaunch.manage.connections.status.${status}`)

const lmsLabel = (lms: string): string => t(`externalLaunch.manage.connections.lms.${lms}`)

const secretLabel = (connection: ConnectionData): string =>
  connection.secretLast4
    ? `•••• ${connection.secretLast4}`
    : t('externalLaunch.manage.connections.secret_missing')

const expiresLabel = (connection: ConnectionData): string =>
  connection.expiresAt
    ? formatDateToMonthShort(connection.expiresAt, false, dateLocale, userTimeZone.value)
    : t('externalLaunch.manage.connections.no_expiry')

const openCreate = () => {
  activeConnection.value = null
  isConnectionDialogVisible.value = true
}

const openEdit = (connection: ConnectionData) => {
  activeConnection.value = connection
  isConnectionDialogVisible.value = true
}

const openCredentials = (connection: ConnectionData) => {
  activeConnection.value = connection
  isCredentialsDialogVisible.value = true
}

const onConnectionSaved = async () => {
  notify(t('externalLaunch.manage.connections.messages.saved'))
  await fetchConnections()
}

const onCredentialsSaved = async () => {
  notify(t('externalLaunch.manage.connections.messages.credentials_saved'))
  await fetchConnections()
}
</script>

<template>
  <div>
    <div class="d-flex justify-space-between align-center flex-wrap gap-4 mb-4">
      <div>
        <h4 class="text-h4">
          {{ t('externalLaunch.manage.connections.title') }}
        </h4>
        <div class="text-body-2 text-medium-emphasis">
          {{ t('externalLaunch.manage.connections.subtitle') }}
        </div>
      </div>
      <VBtn
        prepend-icon="tabler-plus"
        @click="openCreate"
      >
        {{ t('externalLaunch.manage.connections.create') }}
      </VBtn>
    </div>

    <VCard>
      <VAlert
        v-if="loadFailed"
        type="error"
        variant="tonal"
        class="ma-4"
      >
        <div class="d-flex align-center justify-space-between flex-wrap gap-3">
          <span>{{ t('externalLaunch.manage.messages.load_error') }}</span>
          <VBtn
            size="small"
            variant="tonal"
            color="error"
            @click="fetchConnections"
          >
            {{ t('retry') }}
          </VBtn>
        </div>
      </VAlert>

      <VDataTable
        v-else
        :headers="headers"
        :items="connections"
        :loading="isLoading"
        :loading-text="t('loading')"
        :items-per-page="25"
        class="text-no-wrap"
      >
        <template #item.accountKey="{ item }">
          <div class="font-weight-medium">
            {{ item.accountKey }}
          </div>
          <div class="text-body-2 text-medium-emphasis">
            {{ lmsLabel(item.lms) }}
          </div>
        </template>

        <template #item.clientId="{ item }">
          {{ item.clientId || '—' }}
        </template>

        <template #item.secretLast4="{ item }">
          {{ secretLabel(item) }}
        </template>

        <template #item.status="{ item }">
          <VChip
            :color="statusColor(item.status)"
            size="small"
          >
            {{ statusLabel(item.status) }}
          </VChip>
        </template>

        <template #item.expiresAt="{ item }">
          {{ expiresLabel(item) }}
        </template>

        <template #item.actions="{ item }">
          <div class="d-flex justify-end">
            <IconBtn @click="openEdit(item)">
              <VTooltip
                location="top"
                activator="parent"
              >
                {{ t('externalLaunch.manage.connections.edit') }}
              </VTooltip>
              <VIcon icon="tabler-edit" />
            </IconBtn>
            <IconBtn @click="openCredentials(item)">
              <VTooltip
                location="top"
                activator="parent"
              >
                {{ t('externalLaunch.manage.connections.set_credentials') }}
              </VTooltip>
              <VIcon icon="tabler-key" />
            </IconBtn>
          </div>
        </template>

        <template #no-data>
          <div class="text-center text-disabled py-6">
            {{ t('externalLaunch.manage.connections.empty') }}
          </div>
        </template>
      </VDataTable>
    </VCard>

    <ConnectionDialog
      v-model:is-dialog-visible="isConnectionDialogVisible"
      :connection="activeConnection"
      @saved="onConnectionSaved"
    />

    <ConnectionCredentialsDialog
      v-model:is-dialog-visible="isCredentialsDialogVisible"
      :connection="activeConnection"
      @saved="onCredentialsSaved"
    />

    <VSnackbar
      v-model="isSnackbarVisible"
      location="top end"
      :color="snackbarColor"
    >
      {{ snackbarText }}
    </VSnackbar>
  </div>
</template>
